import * as React from "react"
import { COUNTRY_TABS } from "../config"
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext"
import { useNavigate, useParams } from "react-router-dom"
import { useWrapConfirm } from "@/modules/shared/hooks/useWrapConfirm"
import { FieldsetSelect } from "@/modules/settings/components/FieldsetSelect"

type Props = {
    selectedCountryValue: string
    isDirty?: boolean
}

export const CountrySelection = ({
    selectedCountryValue,
    isDirty = false,
}: Props) => {
    const { t } = useLocalize()
    const { companyId } = useParams<{ companyId: string }>()
    const navigate = useNavigate()
    const wrapConfirm = useWrapConfirm()

    const countryOptions = React.useMemo(
        () =>
            COUNTRY_TABS.map((c) => ({
                key: c.name,
                value: c.name,
                label: t(`configuration.minimumWages.${c.name}`).toString(),
            })),
        [t],
    )

    const handleCountrySelect = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const nextName = e.target.value
            const selectedTab = COUNTRY_TABS.find((c) => c.name === nextName)
            if (!selectedTab) return

            const nextPath = `/et/company/${companyId}/configuration/minimum-wage/${selectedTab.name}`

            const navigateToCountry = () => {
                navigate(nextPath)
            }

            if (isDirty) {
                wrapConfirm(navigateToCountry, {
                    message: t("configuration.unsaved-changes-close-modal-confirm"),
                })()
                return
            }

            navigateToCountry()
        },
        [isDirty, companyId, navigate, t, wrapConfirm],
    )

    return (
        <div className="tw-px-4 tw-my-6 tw-w-full tw-max-w-[420px] tw-z-20">
            <FieldsetSelect
                label={t("configuration.minimumWages.chooseTab")}
                placeholder={t("newDelegation.choose-country")}
                value={selectedCountryValue}
                name="minimum-wage-country"
                onChange={handleCountrySelect}
                options={countryOptions}
                searchEnabled
            />
        </div>
    )
}